import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

export default function ProfileSetup() {
  const [area, setArea] = useState('');
  const [childAge, setChildAge] = useState('');
  const [careType, setCareType] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user, profile, updateProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/auth/login');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (profile?.avatar_url) {
      setAvatarUrl(profile.avatar_url);
    }
  }, [profile]);

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ファイルサイズチェック（2MB以下）
    if (file.size > 2 * 1024 * 1024) {
      alert('画像サイズは2MB以下にしてください');
      return;
    }

    // ファイル形式チェック
    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください');
      return;
    }

    setAvatarFile(file);
    
    // プレビュー表示
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile || !user) return avatarUrl;

    setUploading(true);
    try {
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      // 古いアバターを削除
      if (avatarUrl) {
        const oldPath = avatarUrl.split('/avatars/')[1];
        if (oldPath) {
          await supabase.storage.from('avatars').remove([oldPath]);
        }
      }

      // 新しいアバターをアップロード
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, avatarFile, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // 公開URLを取得
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      return data.publicUrl;
    } catch (error) {
      console.error('アバターアップロードエラー:', error);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/auth/login');
      return;
    }

    setLoading(true);

    try {
      // アバターをアップロード
      const newAvatarUrl = await uploadAvatar();

      await updateProfile({
        area,
        child_age: childAge,
        care_type: careType,
        ...(newAvatarUrl && { avatar_url: newAvatarUrl }),
      });
      navigate('/board');
    } catch (err) {
      console.error('プロフィール更新エラー:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/board');
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!user) {
    return null;
  }

  const displayAvatar = avatarPreview || avatarUrl;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50 flex items-center justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">プロフィール設定</h1>
            <p className="text-sm sm:text-base text-gray-600">任意項目です。後から変更できます</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* アバター画像アップロード */}
            <div className="flex flex-col items-center">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-3">
                プロフィール画像
              </label>
              <div className="relative group">
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-teal-100 shadow-lg">
                  {displayAvatar ? (
                    <img
                      src={displayAvatar}
                      alt="プロフィール画像"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-100 to-teal-200">
                      <img 
                        src="https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/abea7e377127d504f93f3b47dac5173e.png" 
                        alt="デフォルトアバター" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-8 h-8 sm:w-10 sm:h-10 bg-teal-600 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-teal-700 transition-colors cursor-pointer"
                >
                  <i className="ri-camera-line text-sm sm:text-base"></i>
                </button>
                {avatarPreview && (
                  <button
                    type="button"
                    onClick={removeAvatar}
                    className="absolute top-0 right-0 w-6 h-6 sm:w-7 sm:h-7 bg-red-500 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-red-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xs sm:text-sm"></i>
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarSelect}
                className="hidden"
              />
              <p className="mt-2 text-xs text-gray-500">
                JPG, PNG形式（2MB以下）
              </p>
            </div>

            <div>
              <label htmlFor="area" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                お住まいの地域
              </label>
              <input
                id="area"
                type="text"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-xs sm:text-sm"
                placeholder="例：宮城県仙台市"
              />
            </div>

            <div>
              <label htmlFor="childAge" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                お子さんの年齢
              </label>
              <input
                id="childAge"
                type="text"
                value={childAge}
                onChange={(e) => setChildAge(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-xs sm:text-sm"
                placeholder="例：2歳"
              />
            </div>

            <div>
              <label htmlFor="careType" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                ケアの種類
              </label>
              <textarea
                id="careType"
                value={careType}
                onChange={(e) => setCareType(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-xs sm:text-sm resize-none"
                placeholder="例：経管栄養、酸素療法など"
              />
              <p className="mt-1 text-xs text-gray-500">
                {careType.length}/500文字
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                type="button"
                onClick={handleSkip}
                className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200 whitespace-nowrap text-sm sm:text-base"
              >
                スキップ
              </button>
              <button
                type="submit"
                disabled={loading || uploading}
                className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-all duration-200 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {loading || uploading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    {uploading ? '画像をアップロード中...' : '保存中...'}
                  </span>
                ) : (
                  '保存して始める'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
